import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Label } from '../ui/label';
import {
  Eye,
  FileText,
  X,
} from 'lucide-react';
import { QuestionAnswerPreview } from './QuestionAnswerPreview';

/** Mirrors LocalQuestion from TemplateBuilder */
interface PreviewQuestion {
  _key: string;
  question_text: string;
  question_type: 'text' | 'textarea' | 'number' | 'date' | 'yes_no' | 'single_choice' | 'multiple_choice' | 'file_upload' | 'rating';
  help_text: string;
  is_required: boolean;
  is_scored: boolean;
  max_score: number;
  choices: string[];
}

/** Mirrors LocalSection from TemplateBuilder */
interface PreviewSection {
  _key: string;
  title: string;
  description: string;
  weight: number;
  is_required: boolean;
  questions: PreviewQuestion[];
}

interface TemplatePreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateName: string;
  description: string;
  instructions: string;
  passScore: number;
  sections: PreviewSection[];
}

/**
 * Preview dialog that shows how a qualification questionnaire will appear to suppliers.
 * Matches the exact layout used in QuestionsTab.tsx and QuestionInput.tsx.
 * Renders from the current in-memory state so unsaved changes are reflected.
 */
export default function TemplatePreview({
  open,
  onOpenChange,
  templateName,
  description,
  instructions,
  passScore,
  sections,
}: TemplatePreviewProps) {
  const totalQuestions = sections.reduce((acc, s) => acc + s.questions.length, 0);
  const totalMaxScore = sections.reduce(
    (sum, s) => sum + s.questions.reduce((qSum, q) => qSum + (q.is_scored ? q.max_score : 0), 0),
    0,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-primary" />
            <DialogTitle>Questionnaire Preview</DialogTitle>
          </div>
          <DialogDescription>
            This is how suppliers will see the questionnaire. All fields are read-only.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Preview Banner */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30 p-3 flex items-center gap-2">
            <Eye className="h-4 w-4 text-black shrink-0" />
            <span className="text-sm text-black">
              Preview Mode — This reflects your current unsaved changes
            </span>
          </div>

          {/* Questionnaire Header */}
          <div>
            <h2 className="text-xl font-bold text-black">{templateName || 'Untitled Questionnaire'}</h2>
            {description && (
              <p className="text-black mt-1">{description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-sm text-black">
              <span>{sections.length} section{sections.length !== 1 ? 's' : ''}</span>
              <span>{totalQuestions} question{totalQuestions !== 1 ? 's' : ''}</span>
              {totalMaxScore > 0 && <span>{totalMaxScore} pts max</span>}
              {passScore > 0 && <span>Pass score: {passScore}%</span>}
            </div>
          </div>

          {/* Instructions */}
          {instructions && (
            <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
              <CardContent className="py-3">
                <p className="text-sm font-medium text-black mb-1">Instructions</p>
                <p className="text-sm text-black whitespace-pre-wrap">
                  {instructions}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Sections — matches QuestionsTab.tsx layout exactly */}
          {sections.length > 0 ? (
            <div className="space-y-6">
              {sections.map((section) => (
                <Card key={section._key}>
                  <CardHeader>
                    <CardTitle>{section.title || 'Untitled Section'}</CardTitle>
                    {section.description && (
                      <CardDescription className="text-black">{section.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {section.questions.length > 0 ? (
                      section.questions.map((question, idx) => (
                        <div key={question._key} className="space-y-2">
                          {/* Question label — matches QuestionsTab.tsx */}
                          <div className="flex items-start justify-between">
                            <Label className="text-base font-medium text-black">
                              {idx + 1}. {question.question_text || 'Untitled question'}
                              {question.is_required && <span className="text-red-500 ml-1">*</span>}
                            </Label>
                            {question.is_scored && (
                              <Badge variant="outline" className="ml-2 shrink-0">
                                0 / {question.max_score} pts
                              </Badge>
                            )}
                          </div>
                          {/* Help text — matches QuestionsTab.tsx */}
                          {question.help_text && (
                            <p className="text-sm text-black">{question.help_text}</p>
                          )}
                          {/* Question input — matches QuestionInput.tsx */}
                          <QuestionAnswerPreview
                            questionType={question.question_type}
                            choices={question.choices}
                            idKey={question._key}
                          />
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-black text-center py-4">
                        No questions in this section yet
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <FileText className="h-12 w-12 text-black mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-black mb-2">No questions available</h3>
                <p className="text-black">
                  Add sections and questions to see them in the preview.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Close button */}
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4 mr-2" />
              Close Preview
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
